import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Message } from '../../types/chat';
import { Socket } from 'socket.io-client';
// import { FileUpload } from './FileUpload';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';
// import { TypingIndicator } from './TypingIndicator';
import { FiVolume2, FiVolumeX } from 'react-icons/fi';

const STATIC_URL = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' && window.supportlyChatConfig?.staticUrl) || '';

const WidgetContainer = styled.div<{ $isMinimized: boolean; $fullScreen?: boolean }>`
  position: ${props => props.$fullScreen ? 'fixed' : 'fixed'};
  top: ${props => props.$fullScreen ? '0' : 'unset'};
  left: ${props => props.$fullScreen ? '0' : 'unset'};
  bottom: ${props => props.$fullScreen ? 'unset' : '20px'};
  right: ${props => props.$fullScreen ? 'unset' : '20px'};
  width: ${props => props.$fullScreen ? '100vw' : '350px'};
  max-width: 100vw;
  /*max-height: 45vh;*/
  height: min(480px, 100%);
  background: linear-gradient(145deg, rgba(18, 18, 18, 0.92) 0%, rgba(35, 35, 35, 0.82) 100%);
  border-radius: ${props => props.$fullScreen ? '0' : '8px'};
  box-shadow: ${props => props.$fullScreen ? 'none' : '0 2px 12px rgba(0,0,0,0.13)'};
  display: flex;
  flex-direction: column;
  transition: all 0.3s cubic-bezier(.4,2,.6,1);
  transform: ${props => props.$isMinimized && !props.$fullScreen ? 'translateY(calc(100% - 40px))' : 'translateY(0)'};
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.14);

  @media (max-width: 600px) {
    width: ${props => props.$fullScreen ? '100vw' : 'calc(100vw - 10px)'};
    height: ${props => props.$fullScreen ? '100vh' : 'calc(100vh - 10px)'};
    bottom: ${props => props.$fullScreen ? '0' : '5px'};
    right: ${props => props.$fullScreen ? '0' : '5px'};
    left: ${props => props.$fullScreen ? '0' : '5px'};
  }
`;

const Header = styled.div<{ $fullScreen?: boolean }>`
  padding: 7px 10px 6px 10px;
  background: rgb(0 0 0 / 85%);
  color: #fff;
  border-radius: ${props => props.$fullScreen ? '0' : '8px 8px 0 0'};
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: ${props => props.$fullScreen ? 'default' : 'pointer'};
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MenuContainer = styled.div`
  position: relative;
`;

const MenuButton = styled.button`
  border: 1px solid rgba(255, 255, 255, 0.25);
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.18);
  }
`;

const HistoryButton = styled(MenuButton)`
  margin-left: 6px;
`;

const MenuDropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  min-width: 220px;
  z-index: 1200;
  background: rgba(10, 10, 10, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.35);
  overflow: hidden;
`;

const MenuItem = styled.button`
  width: 100%;
  border: none;
  background: transparent;
  color: #fff;
  text-align: left;
  padding: 10px 12px;
  font-size: 0.85rem;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }
`;

const ControlButton = styled.button`
  background: none;
  border: none;
  color: #fff;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.8;
  }
`;

const MinimizeButton = styled.button`
  background: rgba(255, 255, 255, 0.12);
  border: none;
  color: #fff;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  transition: background-color 0.2s;
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }
`;

const ChatContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: linear-gradient(165deg, rgba(28, 28, 28, 0.86) 0%, rgba(51, 51, 51, 0.72) 100%);
  border-radius: 0 0 5px 5px;
`;

const MessageBadge = styled.div`
  font-size: 1rem;
  padding: 4px 10px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
  font-weight: 700;
  display: inline-block;
`;

interface ChatWidgetProps {
  messages: Message[];
  onSendMessage: (content: string, file?: File) => void;
  archiveConversations?: Array<{
    date: string;
    messageCount: number;
    preview: string;
  }>;
  activeConversationDate?: string | null;
  onOpenArchivedConversation?: (date: string) => void;
  onOpenActiveConversation?: () => void;
  onConsentAnswer?: (answer: 'yes' | 'no') => void;
  consentQuestion?: string | null;
  consentSubmitting?: boolean;
  activeConsentCase?: {
    hasActiveCase: boolean;
    status: string | null;
  };
  socket: Socket | null;
  error: string | null;
  typingUsers: string[];
  onTyping: (sessionId: string) => void;
  onStopTyping: (sessionId: string) => void;
  currentUserId: string;
  sessionId: string;
  fullScreen?: boolean;
  userName: string;
  onOpen?: () => void;
  onClose?: () => void;
  soundEnabled: boolean;
  setSoundEnabled: (enabled: boolean) => void;
}

export const ChatWidget: React.FC<ChatWidgetProps & { loadMore?: () => void; hasMore?: boolean }> = ({
  messages,
  onSendMessage,
  archiveConversations = [],
  activeConversationDate = null,
  onOpenArchivedConversation,
  onOpenActiveConversation,
  onConsentAnswer,
  consentQuestion,
  consentSubmitting = false,
  activeConsentCase,
  socket,
  error,
  typingUsers,
  onTyping,
  onStopTyping,
  currentUserId,
  sessionId,
  fullScreen = false,
  userName,
  onOpen,
  onClose,
  soundEnabled,
  setSoundEnabled,
  loadMore,
  hasMore
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout>();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const prevMessagesLength = useRef(messages.length);
  const wasOpen = useRef(isOpen);

  useEffect(() => {
    if (isOpen && onOpen) {
      onOpen();
    }
  }, [isOpen, onOpen]);

  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen]);

  useEffect(() => {
    if (!socket || !sessionId || !currentUserId || !userName) return;

    const handleConnect = () => {
      socket.emit('user_online', {
        sessionId,
        userId: currentUserId,
        name: userName,
        isAnonymous: true
      });
    };

    socket.on('connect', handleConnect);

    // If already connected — send immediately
    if (socket.connected) {
      handleConnect();
    }

    return () => {
      socket.off('connect', handleConnect);
    };
  }, [socket, sessionId, currentUserId, userName]);

  const handleTyping = () => {
    if (!socket) return;
    socket.emit('typing_start', { sessionId });
    if (typeof onTyping === 'function') onTyping(sessionId);
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = setTimeout(() => {
      onStopTyping(sessionId);
    }, 3000);
  };

  const handleStopTyping = () => {
    if (!socket) return;
    socket.emit('typing_stop', { sessionId });
    if (typeof onStopTyping === 'function') onStopTyping(sessionId);
  };

  const handleSendMessage = (content: string, file?: File) => {
    console.log('Sending message:', { content, hasFile: !!file });
    onSendMessage(content, file);
  };

  const isViewingArchive = Boolean(activeConversationDate);
  const hasActiveConsent = Boolean(activeConsentCase?.hasActiveCase);

  const toggleSound = () => {
    const newSoundState = !soundEnabled;
    setSoundEnabled(newSoundState);
    localStorage.setItem('soundEnabled', String(newSoundState));
  };

  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      prevMessagesLength.current = messages.length;
    }
    wasOpen.current = isOpen;
  }, [isOpen, messages.length]);

  useEffect(() => {
    if (!soundEnabled) return;
    if (!audioRef.current) return;
    if (messages.length <= prevMessagesLength.current) return;
    const lastMsg = messages[messages.length - 1];
    let senderId = typeof lastMsg.sender === 'object' && lastMsg.sender !== null && 'id' in lastMsg.sender
      ? (lastMsg.sender as Record<string, any>).id
      : String(lastMsg.sender);
    if (senderId !== currentUserId && isOpen) {
      audioRef.current.currentTime = 0;
      audioRef.current.play();
    }
    prevMessagesLength.current = messages.length;
  }, [messages, soundEnabled, currentUserId, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
        setIsHistoryOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const exportChatAsText = () => {
    const header = [
      'NFG SUPPORT CHAT EXPORT',
      `Sessie: ${sessionId}`,
      `Gegenereerd: ${new Date().toISOString()}`,
      '',
    ];

    const lines = messages.map((message) => {
      const timestamp = message.timestamp ? new Date(message.timestamp).toISOString() : new Date().toISOString();
      const senderName = message.senderName || 'Onbekend';
      const fileLine = message.fileName ? ` [BESTAND: ${message.fileName}]` : '';
      const content = (message.content || '').replace(/\s+/g, ' ').trim();
      return `[${timestamp}] ${senderName}: ${content}${fileLine}`;
    });

    const text = [...header, ...lines, ''].join('\n');
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    const safeSession = String(sessionId || 'chat').replace(/[^a-zA-Z0-9-_]/g, '_');
    const filename = `nfg-chat-export-${safeSession}.txt`;

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    setIsMenuOpen(false);
  };

  useEffect(() => {
    // Unlock audio playback after first user interaction (required by browsers)
    const unlockAudio = () => {
      if (audioRef.current) {
        audioRef.current.volume = 0;
        audioRef.current.play();
        audioRef.current.volume = 1;
      }
      window.removeEventListener('click', unlockAudio);
      window.removeEventListener('keydown', unlockAudio);
    };
    window.addEventListener('click', unlockAudio);
    window.addEventListener('keydown', unlockAudio);
    return () => {
      window.removeEventListener('click', unlockAudio);
      window.removeEventListener('keydown', unlockAudio);
    };
  }, []);

  return (
    <WidgetContainer $isMinimized={false} $fullScreen={fullScreen}>
      <Header $fullScreen={fullScreen}>
        <MenuContainer ref={menuRef}>
          <MenuButton
            type="button"
            onClick={() => {
              setIsMenuOpen(v => !v);
              setIsHistoryOpen(false);
            }}
          >
            Menu
          </MenuButton>
          <HistoryButton
            type="button"
            onClick={() => {
              setIsHistoryOpen(v => !v);
              setIsMenuOpen(false);
            }}
          >
            Geschiedenis
          </HistoryButton>
          {isMenuOpen && (
            <MenuDropdown>
              {hasActiveConsent && (
                <MenuItem
                  type="button"
                  onClick={() => {
                    onOpenActiveConversation?.();
                    setIsMenuOpen(false);
                  }}
                >
                  Open vragenlijst hervatten
                </MenuItem>
              )}
              {isViewingArchive && (
                <MenuItem
                  type="button"
                  onClick={() => {
                    onOpenActiveConversation?.();
                    setIsMenuOpen(false);
                  }}
                >
                  Terug naar vandaag
                </MenuItem>
              )}
              <MenuItem type="button" onClick={exportChatAsText}>
                Exporteer chat
              </MenuItem>
            </MenuDropdown>
          )}
          {isHistoryOpen && (
            <MenuDropdown>
              {archiveConversations.length > 0 &&
                archiveConversations.map((conversation) => (
                  <MenuItem
                    key={conversation.date}
                    type="button"
                    onClick={() => {
                      onOpenArchivedConversation?.(conversation.date);
                      setIsHistoryOpen(false);
                    }}
                  >
                    Gesprek {conversation.date} ({conversation.messageCount})
                  </MenuItem>
                ))}
              {archiveConversations.length === 0 && (
                <MenuItem type="button" disabled>
                  Geen eerdere gesprekken
                </MenuItem>
              )}
            </MenuDropdown>
          )}
        </MenuContainer>
        <HeaderActions>
          <ControlButton
            onClick={e => { e.stopPropagation(); toggleSound(); }}
            style={{ opacity: soundEnabled ? 1 : 0.5 }}
            title={soundEnabled ? 'Geluid uitschakelen' : 'Geluid inschakelen'}
          >
            {soundEnabled ? <FiVolume2 /> : <FiVolumeX />}
          </ControlButton>
        </HeaderActions>
      </Header>
      <audio ref={audioRef} src={`${STATIC_URL}/notification.mp3`} preload="auto" />
      <ChatContent>
        <MessageList
          messages={messages}
          currentUserId={currentUserId}
          typingUsers={typingUsers}
          isAdminView={false}
          onLoadMore={loadMore}
          hasMore={hasMore}
        />
        <MessageInput
          onSendMessage={handleSendMessage}
          onTyping={handleTyping}
          onStopTyping={handleStopTyping}
          onConsentAnswer={onConsentAnswer}
          consentQuestion={consentQuestion}
          consentSubmitting={consentSubmitting}
          disabled={isViewingArchive}
          sessionId={sessionId}
        />
        <div ref={messagesEndRef} />
      </ChatContent>
    </WidgetContainer>
  );
}; 
